import { DatafeedSocket } from '../../bot/adapters/datafeedSocket'
import { fetchCandlesBefore } from '../../bot/adapters/candleSocket'
import { fetchBacktestEvents } from '../../bot/adapters/eventFetcher'
import type { CandleData } from '../../bot/types/chartData'
import {
  displaySpan,
  PAIR_WINDOW_BARS,
  VISIBLE_LEAD_BARS,
  type DisplaySpan,
  type DrawingSpan,
  type DrawnEntry,
} from './walkthroughPeriod'

/**
 * Bars before the visible window handed to the replay chart's indicators.
 *
 * The chart shows nothing before the first drawing, but an MA(200) drawn from the chart's own
 * candles would then be missing for the first 200 of them — which is most of a walkthrough. The
 * indicators compute over this history plus the chart's bars, and only the chart's part is drawn
 * (see useWalkthroughIndicators). Generous so a long EMA has settled, bounded so every recalc
 * stays cheap: the forming-bar animation recalculates on every frame.
 */
export const INDICATOR_HISTORY_BARS = 1000

export interface WalkthroughDisplay extends DisplaySpan {
  /** Candles before `startTs`, oldest first. Indicator warm-up only — never drawn. */
  history: CandleData[]
}

export interface PrepareWalkthroughDisplayOptions {
  btid: string
  symbol: string
  timeframe: string
  span: DrawingSpan
  /** The drawn entries — see drawnEntries. */
  entries: DrawnEntry[]
  /** The run's REAL range, unix seconds. */
  rangeStartTs: number
  rangeEndTs: number
}

/**
 * Work out the visible window for a FINISHED run, and fetch what the chart needs before it.
 *
 * Only possible once the run is complete: the window's end depends on where the code actually
 * traded (see `displaySpan`). Two requests, in parallel:
 *
 *  - the run's order events over its REAL range — the same request the trades pane makes, so
 *    it answers from the event cache there;
 *  - the candles up to the first drawing. They place the window's first bar on a real candle —
 *    up to PAIR_WINDOW_BARS before the first drawing, when the code's trade for it came early —
 *    and everything before that is indicator history.
 *
 * Never rejects. Either request failing degrades rather than blocks: no events means a window
 * that runs to the run's end, no candles means a wall-clock lead and cold indicators — both
 * still a working replay.
 */
export async function prepareWalkthroughDisplay({
  btid,
  symbol,
  timeframe,
  span,
  entries,
  rangeStartTs,
  rangeEndTs,
}: PrepareWalkthroughDisplayOptions): Promise<WalkthroughDisplay> {
  const firstDrawingSec = Math.floor(span.fromTs / 1000)
  const [orders, before] = await Promise.all([
    fetchBacktestEvents(btid, rangeStartTs, rangeEndTs),
    candlesBefore(
      symbol,
      timeframe,
      firstDrawingSec,
      INDICATOR_HISTORY_BARS + PAIR_WINDOW_BARS + VISIBLE_LEAD_BARS + 1,
    ),
  ])

  const window = displaySpan(span, timeframe, rangeStartTs, rangeEndTs, { orders, entries, candles: before })

  return { ...window, history: before.filter((c) => c.time < window.startTs) }
}

/** `count` bars at or before `toTs`, or none — see prepareWalkthroughDisplay for why none is fine. */
async function candlesBefore(
  symbol: string,
  timeframe: string,
  toTs: number,
  count: number,
): Promise<CandleData[]> {
  let socket: DatafeedSocket | null = null
  try {
    socket = new DatafeedSocket()
    return await fetchCandlesBefore(socket, symbol, timeframe, toTs, count)
  } catch {
    return []
  } finally {
    socket?.dispose()
  }
}
